import {animate, style, transition, trigger} from '@angular/animations';
import {
  afterRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {ActivatedRoute} from '@angular/router';
import {Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {filter, map, switchMap, take} from 'rxjs';
import {CSChallengeChart, CSChallengeTask} from 'src/app/core/model/project';
import {
  GetChallengeCycleDayAction,
  GetCSChallengeDayDetailAction,
} from 'src/app/core/store/current-sked/current-sked.action';
import {
  selectChallengeById,
  selectChallengeDaysById,
  selectChartDetailFor,
  selectChartDetailLoadingMap,
  selectChartDetailMap,
} from 'src/app/core/store/current-sked/current-sked.selector';
import {TaskStatuses} from 'src/app/current-sked/shared/task-statuses';
import {
  CbScheduleChipComponent,
  ScheduleChipType,
} from 'src/app/projects/challenge-builder/shared/cb-schedule-chip.component';
import {TimezoneService} from 'src/app/services/timezone.service';
import {getColorForName} from 'src/app/shared/utils/name-to-color.util';
import {TrackerMetricValuePipe} from 'src/app/shared/pipes/tracker-metric-value.pipe';
import {CsChallengeDayCycleSelectorComponent} from '../shared/cs-challenge-day-cycle-selector/cs-challenge-day-cycle-selector.component';
import {
  CsChallengesMetricUpdateDialogComponent,
  CsChallengesMetricUpdateDialogData,
} from './cs-challenges-metric-update-dialog/cs-challenges-metric-update-dialog.component';
import {
  CsDayChartDetailDialogComponent,
  CsDayChartDetailDialogData,
} from './chart-detail/cs-day-chart-detail-dialog.component';
import {MobileService} from 'src/app/core/page/mobile.service';

const CONTENT_ANIMATION_DURATION = '220ms';

@Component({
  selector: 'cs-challenges-tasks',
  standalone: true,
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    CbScheduleChipComponent,
    CsChallengeDayCycleSelectorComponent,
    TrackerMetricValuePipe,
  ],
  templateUrl: './cs-challenges-tasks.component.html',
  styleUrl: './cs-challenges-tasks.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideContent', [
      transition(':decrement', [
        style({transform: 'translateX(-50px)', opacity: 0}),
        animate(
          `${CONTENT_ANIMATION_DURATION} cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
          style({transform: 'translateX(0)', opacity: 1})
        ),
      ]),
      transition(':increment', [
        style({transform: 'translateX(50px)', opacity: 0}),
        animate(
          `${CONTENT_ANIMATION_DURATION} cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
          style({transform: 'translateX(0)', opacity: 1})
        ),
      ]),
    ]),
  ],
})
export class CsChallengesTasksComponent implements OnInit {
  private readonly mobileService = inject(MobileService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly timezoneService = inject(TimezoneService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);

  /** If the parent has a persisted day, pass it in so we resume there. */
  readonly initialDay = input<moment.Moment | null>(null);
  readonly selectedDayChange = output<moment.Moment>();

  private readonly challengeId = signal<string | null>(null);

  private readonly challenge = toSignal(
    this.activatedRoute.paramMap.pipe(
      map(params => params.get('id')),
      filter(id => !!id),
      switchMap(id => this.store.select(selectChallengeById(id)))
    )
  );

  private readonly challengeDays = toSignal(
    this.activatedRoute.paramMap.pipe(
      map(params => params.get('id')),
      filter(id => !!id),
      switchMap(id => this.store.select(selectChallengeDaysById(id)))
    )
  );

  public readonly selectedCycleDayMoment = signal<moment.Moment | null>(null);
  public readonly todayMoment = signal<moment.Moment | null>(null);
  public readonly loadingDirection = signal<'prev' | 'next' | null>(null);

  public readonly selectedCycleDay = computed(() => {
    const dateKey = this.selectedCycleDayMoment()?.format('YYYY-MM-DD');
    const days = this.challengeDays();
    return dateKey && days ? days[dateKey] : undefined;
  });

  public readonly selectedCycleDayDateKey = computed(() => this.selectedCycleDayMoment()?.format('YYYY-MM-DD') ?? '');

  public readonly chartDetailMap = this.store.selectSignal(selectChartDetailMap);
  public readonly chartDetailLoadingMap = this.store.selectSignal(selectChartDetailLoadingMap);

  public readonly tasks = computed(() => this.selectedCycleDay()?.tasks);
  public readonly charts = computed(() => this.selectedCycleDay()?.charts);

  readonly dayDisplayText = computed(() => {
    const day = this.selectedCycleDayMoment();
    return day ? day.format('dddd, MMMM D') : '';
  });

  readonly isPrevDayDisabled = computed(() => {
    const selectedDay = this.selectedCycleDayMoment();
    const challenge = this.challenge();
    if (!selectedDay || !challenge?.startDateTime) return true;
    const userTz = this.timezoneService.getTimeZone();
    const startMoment = moment.tz(challenge.startDateTime, userTz).startOf('day');
    return selectedDay.isSameOrBefore(startMoment, 'day');
  });

  readonly isNextDayDisabled = computed(() => {
    const m = this.selectedCycleDayMoment();
    const today = this.todayMoment();
    return m && today ? m.isSameOrAfter(today, 'day') : false;
  });

  private readonly todayCycleNumber = computed(() => {
    const today = this.todayMoment();
    const dateKey = today?.format('YYYY-MM-DD');
    const days = this.challengeDays();
    const todayDay = dateKey && days ? days[dateKey] : undefined;
    return todayDay?.cycleNumber ?? null;
  });

  public readonly areTasksDisabled = computed(() => {
    const currentCycle = this.todayCycleNumber();
    const selectedDay = this.selectedCycleDay();
    if (currentCycle == null || !selectedDay?.cycleNumber) return false;
    return currentCycle - selectedDay.cycleNumber > 1;
  });

  private readonly chartsScrollEl = viewChild<ElementRef<HTMLElement>>('chartsScroll');
  readonly scrollFade = signal<'none' | 'right' | 'left' | 'both'>('none');

  readonly circumference = 2 * Math.PI * 36;
  readonly TaskStatuses = TaskStatuses;

  chartCompletionMap = computed<Map<string, boolean>>(() => {
    const chartsData = this.charts() ?? [];
    const map = new Map<string, boolean>();
    for (const chart of chartsData) {
      map.set(chart.name, chart.currentValue >= chart.targetValue);
    }
    return map;
  });

  chartDashOffsetMap = computed<Map<string, number>>(() => {
    const chartsData = this.charts() ?? [];
    const map = new Map<string, number>();
    for (const chart of chartsData) {
      const progress = chart.targetValue > 0 ? Math.min(chart.currentValue / chart.targetValue, 1) : 0;
      map.set(chart.name, this.circumference * (1 - progress));
    }
    return map;
  });

  chartProgressColorMap = computed<Map<string, string>>(() => {
    const chartsData = this.charts() ?? [];
    const map = new Map<string, string>();
    for (const chart of chartsData) {
      map.set(chart.name, getColorForName(chart.name, 'metric'));
    }
    return map;
  });

  constructor() {
    effect(() => {
      const day = this.selectedCycleDayMoment();
      if (day) this.selectedDayChange.emit(day);
    });

    afterRender(() => {
      this.updateScrollFade();
    });
  }

  ngOnInit(): void {
    const tz = this.timezoneService.getTimeZone();
    const today = moment().tz(tz).startOf('day');
    this.todayMoment.set(today);

    const initial = this.initialDay();
    this.selectedCycleDayMoment.set(initial ? initial.clone() : today.clone());

    this.activatedRoute.paramMap
      .pipe(
        map(params => params.get('id')),
        filter(id => !!id),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(id => {
        this.challengeId.set(id);
      });
  }

  public onPrevDay(): void {
    const id = this.challengeId();
    if (!id || this.loadingDirection() !== null) return;

    const current = this.selectedCycleDayMoment();
    if (!current) return;
    const newMoment = current.clone().subtract(1, 'day');
    this.selectedCycleDayMoment.set(newMoment);

    const dateKey = newMoment.format('YYYY-MM-DD');
    const days = this.challengeDays();
    const isAlreadyLoaded = !!days?.[dateKey];

    if (!isAlreadyLoaded) {
      this.loadingDirection.set('prev');
    }

    const timestamp = this.timezoneService.convertMomentToUtcString(newMoment);
    this.store.dispatch(
      new GetChallengeCycleDayAction({
        challengeId: id,
        timestamp,
        onSuccess: () => this.loadingDirection.set(null),
        onFailure: () => this.loadingDirection.set(null),
      })
    );
  }

  public onNextDay(): void {
    const id = this.challengeId();
    if (!id || this.loadingDirection() !== null) return;

    const current = this.selectedCycleDayMoment();
    const today = this.todayMoment();
    if (!current || (today && current.isSameOrAfter(today, 'day'))) return;

    const newMoment = current.clone().add(1, 'day');
    this.selectedCycleDayMoment.set(newMoment);

    const dateKey = newMoment.format('YYYY-MM-DD');
    const days = this.challengeDays();
    const isAlreadyLoaded = !!days?.[dateKey];

    if (!isAlreadyLoaded) {
      this.loadingDirection.set('next');
    }

    const timestamp = this.timezoneService.convertMomentToUtcString(newMoment);
    this.store.dispatch(
      new GetChallengeCycleDayAction({
        challengeId: id,
        timestamp,
        onSuccess: () => this.loadingDirection.set(null),
        onFailure: () => this.loadingDirection.set(null),
      })
    );
  }

  isCompleted(task: CSChallengeTask): boolean {
    return task.status === TaskStatuses.Completed;
  }

  getScheduleType(schedule: string): ScheduleChipType {
    const valid: ScheduleChipType[] = ['morning', 'afternoon', 'evening', 'night', 'multiple'];
    return valid.includes(schedule as ScheduleChipType) ? (schedule as ScheduleChipType) : null;
  }

  openMetricUpdateDialog(task: CSChallengeTask): void {
    const projectId = this.challengeId();
    if (!projectId) return;

    const selected = this.selectedCycleDayMoment();
    const refetchCurrentDay =
      selected != null
        ? {
            challengeId: projectId,
            timestamp: this.timezoneService.convertMomentToUtcString(selected),
          }
        : undefined;

    const dialogData: CsChallengesMetricUpdateDialogData = {
      task,
      projectId,
      refetchCurrentDay,
      challengeTimezone: this.challenge()?.timezone,
    };

    this.dialog.open(CsChallengesMetricUpdateDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: dialogData,
      panelClass: 'cs-challenges-metric-update-dialog',
      width: '100%',
      maxWidth: '500px',
      disableClose: true,
    });
  }

  onChartsScroll(): void {
    this.updateScrollFade();
  }

  private updateScrollFade(): void {
    const el = this.chartsScrollEl()?.nativeElement;
    if (!el) {
      this.scrollFade.set('none');
      return;
    }

    const {scrollLeft, scrollWidth, clientWidth} = el;
    const hasOverflow = scrollWidth > clientWidth + 1;

    if (!hasOverflow) {
      this.scrollFade.set('none');
      return;
    }

    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    if (atStart) this.scrollFade.set('right');
    else if (atEnd) this.scrollFade.set('left');
    else this.scrollFade.set('both');
  }

  openChartDetailDialog(chart: CSChallengeChart): void {
    const projectId = this.challengeId();
    const selected = this.selectedCycleDayMoment();
    if (!projectId || !selected) return;
    const dateKey = selected.format('YYYY-MM-DD');
    const metricId = chart._id;
    const timestamp = this.timezoneService.convertMomentToUtcString(selected);

    this.store
      .select(selectChartDetailFor(dateKey, metricId))
      .pipe(take(1))
      .subscribe(detail => {
        const hasData = !!detail;
        this.store.dispatch(
          new GetCSChallengeDayDetailAction({
            projectId,
            dateKey,
            metricId,
            timestamp,
            onSuccess: hasData
              ? undefined
              : () => this.openChartDetailDialogWithData(projectId, metricId, timestamp, dateKey),
          })
        );
        if (hasData) {
          this.openChartDetailDialogWithData(projectId, metricId, timestamp, dateKey);
        }
      });
  }

  private openChartDetailDialogWithData(projectId: string, metricId: string, timestamp: string, dateKey: string): void {
    const dialogData: CsDayChartDetailDialogData = {
      projectId,
      metricId,
      timestamp,
      dateKey,
    };
    this.dialog.open(CsDayChartDetailDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      data: dialogData,
      panelClass: 'cs-day-chart-detail-dialog',
      width: '100%',
      maxWidth: '400px',
    });
  }
}
